'use client';

import '@/styles/news-styles.css';

import { faFacebook, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpFromBracket, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { YouTubeEmbed } from '@next/third-parties/google';
import ExtraSmallNewsCard from '@pickleballinc/features/src/hub-cards/ExtraSmallNewsCard/ExtraSmallNewsCard';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Modal, ModalContent } from '@pickleballinc/react-ui';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { renderContentWithAd } from '@pickleballinc/ui/components/global/utils/renderContentWithAd';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { Fragment, useEffect, useRef, useState } from 'react';

import EditButton from '@/modules/blog/article_components/EditButton';
import { FavoriteButton } from '@/modules/blog/article_components/FavoriteButton';
import ImagePreviewAndZoom from '@/modules/blog/article_components/ImagePreviewAndZoom';
import { MemeArticleCard } from '@/modules/blog/article_components/MemeArticleCard';
import { PictureArticleCard } from '@/modules/blog/article_components/PictureArticleCard';
import ShareDropdown from '@/modules/blog/article_components/ShareDropdown';
import { VideoArticleCard } from '@/modules/blog/article_components/VideoArticleCard';
import { MEME_SLUG, MEME_SUBCATEGORY_LABELS } from '@/modules/blog/constants';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { ArticlePagination } from '@/types/blog.types';
import { Favorites } from '@/utils/actions/blog/areTheyFavorite';
import type { MainContentNewsInterface } from '@/utils/actions/homepage/getNewsBySlug';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';
import { getWithBaseUrl } from '@/utils/helpers/getWithBaseUrl';

export interface ImgInfo {
  src: string;
  alt: string;
}

export interface MainContentMemeProps {
  news: MainContentNewsInterface;
  relatedNews: ArticlePagination[];
  favorite?: Favorites;
  totalFavoritesCount: string;
  ipAddress: string;
  subcategory: string;
}

export const MainContentMeme = ({
  news,
  relatedNews,
  favorite,
  totalFavoritesCount,
  subcategory,
}: MainContentMemeProps) => {
  const {
    title,
    authorFullName,
    content,
    facebookLink,
    imageAltText,
    imageCaption,
    imageSource,
    imageUrl,
    publishDateDisplayed,
    smsLink,
    twitterLink,
    newsCategory,
    slug,
    uuid,
    videoUrl,
  } = news;
  const newsCategorySlug = newsCategory?.slug || MEME_SLUG;
  const searchParams = useSearchParams();

  const articleURL = getWithBaseUrl(`/${newsCategorySlug}/${slug}`);

  const { session } = useSession();
  const loggedIn = session?.user;

  const { hasPermission } = useAuth();
  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

  const shareDropdownRef = useRef<HTMLUListElement>(null);

  const [shareDropdownOpened, setShareDropdownOpened] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isFavorited, setIsFavorited] = useState(favorite?.moduleUuid !== undefined);
  const [favoritesCount, setFavoritesCount] = useState(
    totalFavoritesCount ? parseInt(totalFavoritesCount) : 0
  );

  const [imgInfo, setImgInfo] = useState<ImgInfo>({ src: '', alt: '' });

  const { beforeAd, afterAd } = renderContentWithAd(content);

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    const target = event.target as HTMLImageElement;
    const imgSrc = target.getAttribute('src') ?? '';
    const imgAlt = target.getAttribute('alt') ?? '';
    setImgInfo({ src: imgSrc, alt: imgAlt });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
        setShareDropdownOpened(false);
        setIsCopied(false);
      }
    };

    document.body.addEventListener('click', handleClickOutside);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const imageContainer = document.getElementById('news-content');

    if (imageContainer) {
      const images = imageContainer.querySelectorAll('img');

      images.forEach((imgElement) => {
        if (imgElement) {
          imgElement.addEventListener('click', handleClick);
        }
      });

      return () => {
        images.forEach((imgElement) => {
          if (imgElement) {
            imgElement.removeEventListener('click', handleClick);
          }
        });
      };
    }

    return () => {};
  }, []);

  useEffect(() => {
    if (window.instgrm) window.instgrm.Embeds.process();
  }, []);

  useEffect(() => {
    if (searchParams?.get('src') === 'pbgo') {
      const topNav = document.querySelector('#pb-topnav');
      const mainHeader = document.querySelector('#pb-mainheader');
      const footer = document.querySelector('#pb-footer');

      if (
        topNav instanceof HTMLElement &&
        mainHeader instanceof HTMLElement &&
        footer instanceof HTMLElement
      ) {
        topNav.style.display = 'none';
        mainHeader.style.display = 'none';
        footer.style.display = 'none';
      }
    }
  }, [searchParams]);

  const youtubeVideoId = videoUrl.split('watch?v=')[1]?.split(/[?=&]/)[0] || '';

  const isMeme = subcategory === MEME_SUBCATEGORY_LABELS.Meme;
  const isPhoto = subcategory === MEME_SUBCATEGORY_LABELS.Photo;
  const isVideo = subcategory === MEME_SUBCATEGORY_LABELS.Video;
  const isPodcast = subcategory === MEME_SUBCATEGORY_LABELS.Podcast;

  return (
    <Fragment>
      {imgInfo.src ? (
        <Modal
          open={imgInfo.src ? true : false}
          onOpenChange={() => setImgInfo({ src: '', alt: '' })}
        >
          <ModalContent className='flex !h-full flex-col items-center justify-center !border-none !bg-transparent !p-4 !outline-none sm:!p-8'>
            <ImagePreviewAndZoom
              imgInfo={imgInfo}
              resetImgURL={() => setImgInfo({ src: '', alt: '' })}
            />
          </ModalContent>
        </Modal>
      ) : null}
      <div className='flex size-full flex-col' itemScope itemType='https://schema.org/NewsArticle'>
        <meta itemProp='url' content={articleURL} />
        <div
          className='flex flex-1 flex-col justify-center overflow-auto pb-4 pt-5 max-w-[704px]'
          id='news-content'
        >
          <div className='w-full'>
            <h2
              className='relative z-30 mb-6 text-3xl font-bold leading-[1.2] text-gray-900 outline-none sm:text-4xl px-4'
              itemProp='headline'
            >
              {title}
            </h2>
            <div className='mb-6 flex items-center gap-2 pb-3 px-4'>
              <div className='flex gap-1'>
                {/* TODO: Add author image when it arrives in payload
                 <Avatar size="sm" /> 
                */}
                <div className='flex flex-col justify-center gap-1'>
                  <div
                    className='relative text-xs font-medium leading-none text-gray-900'
                    itemScope
                    itemProp='author'
                    itemType='https://schema.org/Person'
                  >
                    <span itemProp='name'>{authorFullName}</span>
                  </div>
                  {publishDateDisplayed ? (
                    <div
                      className='relative text-xs leading-none text-[#344054]'
                      itemProp='datePublished'
                      content={moment(publishDateDisplayed).format('YYYY–MM-DD HH:mm:ss')}
                    >
                      {formatDateTime(publishDateDisplayed, {
                        timezone: 'America/New_York',
                        timezoneAbbreviation: 'ET',
                        type: 'datetime',
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className='flex items-center gap-3 sm:justify-center'>
                {facebookLink ? (
                  <a
                    href={facebookLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to Facebook (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faFacebook} size='xl' />
                  </a>
                ) : null}
                {twitterLink ? (
                  <a
                    href={twitterLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to X (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faXTwitter} size='xl' />
                  </a>
                ) : null}
                {smsLink ? (
                  <a
                    href={smsLink}
                    rel='noopener noreferrer'
                    target='_blank'
                    aria-label='Link to SMS (opens in a new tab)'
                  >
                    <FontAwesomeIcon className='text-gray-900' icon={faEnvelope} size='xl' />
                  </a>
                ) : null}
              </div>
            </div>
            {isVideo || isPodcast ? (
              <div className='w-full aspect-video rounded-l-xl relative px-4'>
                <YouTubeEmbed
                  videoid={youtubeVideoId}
                  params='rel=0'
                  style='position: absolute; top: 0; left: 0; width: 100%; height: 100%;'
                />
              </div>
            ) : isMeme ? (
              <figure className='mb-4 w-full flex flex-col justify-center overflow-hidden rounded-lg px-4'>
                <img
                  src={imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 300, 300)}`}
                  alt={imageAltText || title}
                  data-default-src={
                    imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 300, 300)}`
                  }
                  className='!min-h-[300px] !max-h-[528px] min-w-[250px] object-contain rounded-lg'
                  itemProp='thumbnail'
                />
                {(imageCaption && imageCaption.length > 0) ||
                (imageSource && imageSource.length > 0) ? (
                  <figcaption className='mt-2.5 block bg-white px-0.5 text-xs leading-4 text-gray-500'>
                    {imageCaption ? <>{imageCaption}&nbsp;</> : null}
                    {imageSource ? (
                      <cite className='not-italic text-[#a5a6a7]'>{imageSource}</cite>
                    ) : null}
                  </figcaption>
                ) : null}
              </figure>
            ) : (
              <figure className='mb-4 w-full flex flex-col justify-center overflow-hidden rounded-lg px-4'>
                <img
                  src={imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 269, 269)}`}
                  alt={imageAltText || title}
                  data-default-src={
                    imageUrl || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 269, 269)}`
                  }
                  className='!min-h-[250px] !max-h-[528px] min-w-[360px] rounded-lg'
                  itemProp='thumbnail'
                />
                {(imageCaption && imageCaption.length > 0) ||
                (imageSource && imageSource.length > 0) ? (
                  <figcaption className='mt-2.5 block bg-white px-0.5 text-xs leading-4 text-gray-500'>
                    {imageCaption ? <>{imageCaption}&nbsp;</> : null}
                    {imageSource ? (
                      <cite className='not-italic text-[#a5a6a7]'>{imageSource}</cite>
                    ) : null}
                  </figcaption>
                ) : null}
              </figure>
            )}
            <div
              className='like-and-share relative mb-12 flex justify-between flex-wrap items-center'
              data-behavior='content_reactions'
              data-contenttitle={title}
            >
              <div className='flex flex-wrap mt-1 px-4'>
                <div className='flex flex-row mr-3 sm:mr-0 sm:-ml-3 sm:pr-0'>
                  <button
                    className='flex cursor-pointer flex-row items-center gap-1.5 rounded-[100px] sm:px-3 sm:py-1.5 sm:hover:bg-gray-100'
                    onClick={() => setShareDropdownOpened(true)}
                    style={{
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowUpFromBracket} size='lg' />
                    <span className='text-sm font-semibold not-italic text-gray-900 sm:leading-5'>
                      Share
                    </span>
                  </button>
                </div>
                {shareDropdownOpened ? (
                  <ShareDropdown
                    shareDropdownRef={shareDropdownRef}
                    isCopied={isCopied}
                    setIsCopied={setIsCopied}
                    articleURL={articleURL}
                    title={title}
                  />
                ) : null}
                {isSuperAdmin ? <EditButton articleSlug={slug} /> : null}
                {loggedIn ? (
                  <FavoriteButton
                    moduleUuid={uuid}
                    isFavorited={isFavorited}
                    setIsFavorited={setIsFavorited}
                    setFavoritesCount={setFavoritesCount}
                    favoritesCount={favoritesCount}
                  />
                ) : null}
              </div>
              {favoritesCount > 0 && (
                <div className='text-xs text-gray-500 mt-1 px-4'>
                  <span className='mr-2'>
                    <FontAwesomeIcon
                      icon={faHeart}
                      size='lg'
                      className='text-error-500 text-[20px]'
                    />
                  </span>
                  {isFavorited
                    ? favoritesCount === 1
                      ? 'You'
                      : `You and ${favoritesCount - 1} ${favoritesCount === 2 ? 'other' : 'others'}`
                    : favoritesCount}
                </div>
              )}
            </div>
            <p className='text-sm font-medium text-brand-700 px-4'>
              {subcategory ? subcategory.charAt(0).toUpperCase() + subcategory.slice(1) : ''}
            </p>
            <div className='news-preview relative' itemProp='text'>
              {beforeAd
                ? (() => {
                    return (
                      <div className='news-preview relative' itemProp='text'>
                        <div className='px-4'>{beforeAd}</div>
                      </div>
                    );
                  })()
                : null}
            </div>
            {/* <RenderVideoAd
              zoneId={ZONE_IDS_PB.FLOATING_VIDEO}
              pageId={100002}
              place={4}
              ipAddress={ipAddress}
              isProd={IS_PRODUCTION}
              containerClassname='mx-auto'
            /> */}
            <div className='news-preview relative' itemProp='text'>
              {afterAd
                ? (() => {
                    return (
                      <div className='news-preview relative' itemProp='text'>
                        <div className='px-4'>{afterAd}</div>
                      </div>
                    );
                  })()
                : null}
            </div>
          </div>
        </div>

        {relatedNews.length > 0 && (
          <div className='mt-6 px-4 pb-4'>
            <h2 className='mb-6 text-xl font-semibold leading-[1.2] text-gray-900 outline-none'>
              Explore more{' '}
              {isMeme ? 'memes' : isPhoto ? 'photos' : isPodcast ? 'podcasts' : 'videos'}
            </h2>
            <div
              className={clsx(
                'columns-1 w-full max-w-2xl gap-4 pb-2',
                isMeme && 'sm:columns-2',
                !isMeme && 'flex flex-col'
              )}
            >
              {relatedNews.map((article) => {
                if (!isVideo && !isPodcast) {
                  return (
                    <CustomLink
                      key={`related-article-${article.slug}`}
                      className={clsx(
                        'block hover:opacity-80 break-inside-avoid',
                        isMeme && '!mb-6'
                      )}
                      href={`/${news.newsCategory.slug}/${article.slug}`}
                      rel='noopener noreferrer'
                      target='_self'
                    >
                      {isPhoto ? (
                        <PictureArticleCard
                          imageUrl={article.imageUrl}
                          alt={article.imageAltText}
                          title={article.title}
                          authorFullName={`${article.authorFullName}`}
                          type='paginationCover'
                          publishedAt={article.publishDateDisplayed}
                          description={article.description}
                        />
                      ) : isMeme ? (
                        <MemeArticleCard
                          imageUrl={article.imageUrl}
                          alt={article.imageAltText}
                          title={article.title}
                          authorFullName={`${article.authorFullName}`}
                          type='paginationCover'
                          publishedAt={article.publishDateDisplayed}
                          description={article.description}
                        />
                      ) : (
                        <ExtraSmallNewsCard
                          oneLineForDescription
                          image={article.imageUrl || ''}
                          imageAltText={article.imageAltText}
                          title={article.title}
                          description={article.description || ''}
                          publishDateDisplayed={article.publishDateDisplayed}
                          author={article.authorFullName || ''}
                        />
                      )}
                    </CustomLink>
                  );
                } else {
                  return (
                    <div
                      key={`related-article-${article.slug}`}
                      className='flex w-full max-h-[245px] justify-center'
                    >
                      <VideoArticleCard
                        videoUrl={`${article.videoUrl}`}
                        title={article.title}
                        authorFullName={`${article.authorFullName}`}
                        type='paginationCover'
                        publishedAt={article.publishDateDisplayed}
                        description={article.description}
                        link={`/${news.newsCategory.slug}/${article.slug}`}
                        subcategoryTitle={
                          subcategory === MEME_SUBCATEGORY_LABELS.Podcast ? 'Podcast' : 'Video'
                        }
                      />
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
      </div>
      <Script
        async
        defer
        strategy='lazyOnload'
        type='text/javascript'
        src='https://www.instagram.com/embed.js'
      />
      <Script
        async
        defer
        strategy='lazyOnload'
        type='text/javascript'
        src='https://platform.twitter.com/widgets.js'
        charSet='utf-8'
      />
    </Fragment>
  );
};
